import type { MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { PlusOutlined, CloseOutlined, PaperClipOutlined, DeleteOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Image, Radio } from 'antd';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { useGetCitiesDataQuery } from '@/api/endpoints/cities';
import { useGetLMSAccountsDataQuery, useGetQuizzesDataQuery } from '@/api/endpoints/lmsData';
import { useCreateSchoolMutation } from '@/api/endpoints/schools';
import { DataSection, InputField, TextAreaField, SelectField, ImageUploadField } from '@/shared/components';
import { ROUTES } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';
import type { ESchoolType } from '@/shared/types';
import { EQuizType, ESchoolBadgeIcons, ESchoolFormat } from '@/shared/types';

import { IconSelectLabel } from '../_components/IconSelectLabel/IconSelectLabel';
import {
  PUBLIC_SCHOOL_VALIDATION_SCHEMA,
  SCHOOL_FORMAT_RADIO_GROUP_DATA,
  SHORT_DESC_INDEX_TO_FIELD_NAME_MAPPER,
} from '../consts';

import styles from './SchoolCreatePage.module.scss';
import type { TPublicSchoolFormData } from './types';

type TProps = {
  schoolType: ESchoolType;
};

export const CreatePublicSchoolComponent = ({ schoolType }: TProps) => {
  const navigate = useNavigate();

  const sendApiRequest = useApiRequest();

  const { data: citiesData = [], isLoading: areCitiesLoading } = useGetCitiesDataQuery();
  const { data: lmsAccountsData = [], isLoading: areLMSAccountsLoading } = useGetLMSAccountsDataQuery();
  const { data: quizzesData = [], isLoading: areQuizzesLoading } = useGetQuizzesDataQuery();

  const [createSchool, { isLoading: isSchoolCreating }] = useCreateSchoolMutation();

  const methods = useForm<TPublicSchoolFormData>({
    defaultValues: {
      title: '',
      format: ESchoolFormat.Online,
      onlineDescription: '',
      offlineDescription: '',
      hybridDescription: '',
      description: '',
      shortDescFirst: '',
      shortDescSecond: '',
      shortDescThird: '',
      shortDescFourth: '',
      logo: null,
      lmsAccountId: undefined,
      duration: '',
      cityIds: [],
      pollId: null,
      testId: null,
      icon: undefined,
    },
    resolver: yupResolver(PUBLIC_SCHOOL_VALIDATION_SCHEMA),
  });

  const { watch, handleSubmit, resetField, clearErrors } = methods;

  const [numberOfShortDescInputs, setNumberOfShortDescInputs] = useState(1);

  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const lmsAccountId = watch('lmsAccountId');
  const logo = watch('logo');
  watch((_, { name }) => {
    if (name === 'lmsAccountId') {
      resetField('pollId');
      resetField('testId');
    }
  });
  const currentFormat = watch('format');

  const citiesList = useMemo(() => citiesData.map(({ id, name }) => ({ value: id, label: name })), [citiesData]);
  const badgeIconsList = useMemo(
    () =>
      Object.values(ESchoolBadgeIcons).map((iconType) => ({
        value: iconType,
        label: <IconSelectLabel iconType={iconType} />,
      })),
    [],
  );
  const lmsAccountsList = useMemo(
    () => lmsAccountsData.map(({ id, name }) => ({ value: id, label: name })),
    [lmsAccountsData],
  );
  const quizzesList = useMemo(
    () =>
      quizzesData.reduce(
        (prevVal, { account_id, id, name }) => {
          if (account_id === lmsAccountId) {
            return [...prevVal, { value: id, label: name }];
          }

          return prevVal;
        },
        [] as { value: number; label: string }[],
      ),
    [quizzesData, lmsAccountId],
  );

  const addShortDescField = useCallback(() => setNumberOfShortDescInputs((prevValue) => prevValue + 1), []);
  const removeShortDescField = useCallback((fieldValue: 'shortDescSecond' | 'shortDescThird' | 'shortDescFourth') => {
    setNumberOfShortDescInputs((prevValue) => prevValue - 1);
    resetField(fieldValue);
  }, []);

  const goBack = () => {
    navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}`);
  };

  const createSchoolHandler = handleSubmit(
    async ({
      title,
      format,
      onlineDescription,
      offlineDescription,
      hybridDescription,
      description,
      duration,
      icon,
      logo,
      cityIds,
      shortDescFirst,
      shortDescSecond,
      shortDescThird,
      shortDescFourth,
      lmsAccountId,
      pollId,
      testId,
    }) => {
      const poll = quizzesList.find(({ value }) => value === pollId);
      const test = quizzesList.find(({ value }) => value === testId);

      const shortDescription = [shortDescFirst, shortDescSecond, shortDescThird, shortDescFourth].filter(
        (desc): desc is string => Boolean(desc),
      );

      if (!lmsAccountId || !logo?.signed_id) {
        return;
      }

      const FORMAT_TO_DESCRIPTION_MAPPER = {
        [ESchoolFormat.Online]: onlineDescription,
        [ESchoolFormat.Offline]: offlineDescription,
        [ESchoolFormat.Hybrid]: hybridDescription,
      };

      const data = {
        name: title,
        format,
        format_description: FORMAT_TO_DESCRIPTION_MAPPER[format],
        description,
        short_description: shortDescription,
        lms_account_id: lmsAccountId,
        icon,
        active: false,
        duration: duration ? Number(duration) : null,
        city_ids: cityIds,
        poll: poll ? { title: poll.label, external_id: String(poll.value), quiz_type: EQuizType.Poll } : null,
        test: test ? { title: test.label, external_id: String(test.value), quiz_type: EQuizType.Test } : null,
        logo: logo.signed_id,
        school_type: schoolType,
      };

      await sendApiRequest({
        successMessage: 'Школа успешно создана',
        successCallback: () => void navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}`),
        apiRequestCallback: () =>
          createSchool({
            data,
          }),
      });
    },
  );

  const removeUploadedImage = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    resetField('logo');
    setImageUrl('');
  };

  const imageRequestCallback = (file: File) => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);
  };

  useEffect(() => {
    clearErrors(['onlineDescription', 'offlineDescription', 'hybridDescription']);
  }, [currentFormat]);

  return (
    <div className={styles['content-wrapper']}>
      <FormProvider {...methods}>
        <DataSection
          titleText="Название"
          isTitleRequired
        >
          <InputField
            fieldName="title"
            placeholder="Название"
          />
        </DataSection>
        <DataSection
          titleText="Изображение школы"
          isTitleRequired
        >
          <ImageUploadField
            accept="image/png,image/jpeg,image/jpg"
            name="Изображение"
            fieldName="logo"
            className={logo ? styles['image-uploaded'] : ''}
            requestCallback={imageRequestCallback}
            showUploadList={false}
          />
          {logo && (
            <>
              <Button
                size="small"
                onClick={() => setIsImagePreviewOpen(true)}
                type="text"
                icon={<PaperClipOutlined />}
                className={styles['image-button-wrapper']}
              >
                {logo.filename}
                <DeleteOutlined
                  className={styles['remove-image-icon']}
                  onClick={removeUploadedImage}
                />
              </Button>
              <Image
                alt="Лого школы"
                style={{ display: 'none' }}
                preview={{
                  open: isImagePreviewOpen,
                  src: imageUrl,
                  onOpenChange: (value) => setIsImagePreviewOpen(value),
                }}
              />
            </>
          )}
        </DataSection>
        <DataSection titleText="Иконка школы">
          <SelectField
            fieldName="icon"
            size="large"
            virtual={false}
            showSearch={false}
            placeholder="Выбрать иконку"
            options={badgeIconsList}
          />
        </DataSection>
        <DataSection titleText="Краткое описание школы">
          {Array.from({ length: numberOfShortDescInputs }).map((_, index) => {
            const fieldName = SHORT_DESC_INDEX_TO_FIELD_NAME_MAPPER[index];

            if (index === 0) {
              return (
                <InputField
                  key={fieldName}
                  fieldName={fieldName}
                  placeholder="Краткое описание школы"
                />
              );
            }

            return (
              <div
                className={styles['short-desc-input-wrapper']}
                key={fieldName}
              >
                <InputField
                  fieldName={fieldName}
                  placeholder="Краткое описание школы"
                />
                <Button
                  onClick={() =>
                    removeShortDescField(fieldName as 'shortDescSecond' | 'shortDescThird' | 'shortDescFourth')
                  }
                  type="dashed"
                  size="middle"
                  className={styles.button}
                  icon={<CloseOutlined />}
                />
              </div>
            );
          })}
          {numberOfShortDescInputs < 4 && (
            <Button
              onClick={addShortDescField}
              type="dashed"
              size="middle"
              className={styles.button}
              icon={<PlusOutlined />}
            >
              Добавить описание
            </Button>
          )}
        </DataSection>
        <DataSection titleText="Полное описание школы">
          <TextAreaField
            fieldName="description"
            placeholder="Полное описание школы"
          />
        </DataSection>
        <DataSection titleText="Длительность школы, месяцев">
          <InputField
            fieldName="duration"
            placeholder="Длительность школы, месяцев"
            style={{ width: '100%' }}
          />
        </DataSection>
        <div className={styles['half-width-section-wrapper']}>
          <DataSection
            titleText="Города школы"
            wrapperClassName={styles['half-width-section']}
          >
            <SelectField
              fieldName="cityIds"
              mode="multiple"
              size="large"
              placeholder="Выбрать город"
              options={citiesList}
              showSearch={{
                optionFilterProp: 'label',
              }}
              loading={areCitiesLoading}
              disabled={areCitiesLoading}
              notFoundContent="По запросу ничего не найдено"
            />
          </DataSection>
          <DataSection
            titleText="Аккаунт школы в LMS"
            wrapperClassName={styles['half-width-section']}
            isTitleRequired
          >
            <SelectField
              fieldName="lmsAccountId"
              size="large"
              placeholder="Выбрать аккаунт"
              options={lmsAccountsList}
              showSearch={{
                optionFilterProp: 'label',
              }}
              loading={areLMSAccountsLoading}
              disabled={areLMSAccountsLoading}
            />
          </DataSection>
        </div>
        <div className={styles['half-width-section-wrapper']}>
          <DataSection
            titleText="Опрос"
            wrapperClassName={styles['half-width-section']}
          >
            <SelectField
              fieldName="pollId"
              size="large"
              placeholder="Выбрать опрос"
              options={quizzesList}
              showSearch={{
                optionFilterProp: 'label',
              }}
              loading={areQuizzesLoading}
              disabled={areQuizzesLoading || !lmsAccountId}
            />
          </DataSection>
          <DataSection
            titleText="Тестовое задание"
            wrapperClassName={styles['half-width-section']}
          >
            <SelectField
              fieldName="testId"
              size="large"
              placeholder="Выбрать тестовое задание"
              options={quizzesList}
              showSearch={{
                optionFilterProp: 'label',
              }}
              loading={areQuizzesLoading}
              disabled={areQuizzesLoading || !lmsAccountId}
            />
          </DataSection>
        </div>
        <p className={styles['surveys-annotation']}>
          После создания школы в LMS куратор сможет создать опросы и тестовые задания, которые затем появятся в этом
          списке для выбора к текущей школе.
        </p>
        <DataSection titleText="Формат">
          <Controller
            name="format"
            render={({ field }) => (
              <Radio.Group
                {...field}
                className={styles['format-wrapper']}
                optionType="default"
              >
                {SCHOOL_FORMAT_RADIO_GROUP_DATA.map(
                  ({ id, data: { format, buttonText, inputTitle, fieldName, inputPlaceholder } }) => (
                    <div
                      key={id}
                      className={styles['format-radio-button-wrapper']}
                    >
                      <Radio.Button value={format}>{buttonText}</Radio.Button>
                      <DataSection titleText={inputTitle}>
                        <InputField
                          fieldName={fieldName}
                          placeholder={inputPlaceholder}
                          disabled={currentFormat !== format}
                        />
                      </DataSection>
                    </div>
                  ),
                )}
              </Radio.Group>
            )}
          />
        </DataSection>
        <div className={styles['buttons-wrapper']}>
          <Button
            onClick={goBack}
            type="dashed"
            size="large"
            className={styles.button}
          >
            Назад
          </Button>
          <Button
            onClick={createSchoolHandler}
            type="primary"
            size="large"
            className={styles.button}
            disabled={isSchoolCreating}
          >
            Сохранить
          </Button>
        </div>
      </FormProvider>
    </div>
  );
};
